import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "@/v2/components/FunnelLayout";
import GlassCard from "@/v2/components/GlassCard";
import {
  EMPTY_INPUTS,
  loadDraft,
  saveDraft,
  submitRealityCheck,
  type CurrentBehaviourChoice,
  type FormDraft,
  type ProblemFrequency,
  type ProblemUrgencyCost,
  type RealityCheckInputs,
  type TestChannel,
  type ValidationState,
} from "@/v2/lib/realityCheckApi";

type RadioOption<T extends string> = { value: T; label: string; hint?: string };

const FREQUENCY_OPTIONS: RadioOption<ProblemFrequency>[] = [
  { value: "daily", label: "Ogni giorno" },
  { value: "weekly", label: "Ogni settimana" },
  { value: "monthly", label: "Ogni mese" },
  { value: "rare", label: "Raramente" },
  { value: "unknown", label: "Non lo so ancora" },
];

const URGENCY_OPTIONS: RadioOption<ProblemUrgencyCost>[] = [
  { value: "low", label: "Non fa nulla di speciale — si adatta e va avanti", hint: "Il problema c'è ma non cambia il modo di lavorare." },
  { value: "medium", label: "Perde tempo a gestirlo manualmente ogni volta", hint: "Ha un workaround, ma costa ore o piccoli errori ricorrenti." },
  { value: "high", label: "Ha già speso soldi o perso clienti per questo", hint: "Ci sono esempi concreti: fatture mancate, clienti andati via, ore pagate inutilmente." },
  { value: "critical", label: "Non riesce a lavorare normalmente senza risolverlo", hint: "Il problema blocca un processo fondamentale del suo lavoro." },
  { value: "unknown", label: "Non lo so ancora" },
];

const BEHAVIOUR_OPTIONS: RadioOption<CurrentBehaviourChoice>[] = [
  {
    value: "none",
    label: "Non fa nulla, si rassegna",
    hint: "Accetta il problema e va avanti senza cercare soluzioni.",
  },
  {
    value: "workaround",
    label: "Lo gestisce a mano ogni volta",
    hint: "Dedica tempo e attenzione ricorrenti a qualcosa che potrebbe essere automatizzato.",
  },
  {
    value: "workaround_free_tools",
    label: "Usa strumenti gratuiti o improvvisati",
    hint: "Excel, WhatsApp, carta e penna — funziona ma è faticoso.",
  },
  {
    value: "paid_solution",
    label: "Paga già qualcosa per gestirlo",
    hint: "Ha trovato una soluzione parziale, ma non è soddisfacente o costa troppo.",
  },
  { value: "unknown", label: "Non sa ancora come lo gestisce" },
];

const VALIDATION_OPTIONS: RadioOption<ValidationState>[] = [
  { value: "none", label: "No, non ho ancora fatto nessun test" },
  { value: "interviewed", label: "Ho parlato con potenziali utenti" },
  { value: "email_collected", label: "Ho raccolto email / persone interessate" },
  { value: "demo_requests", label: "Ho ricevuto richieste di demo o call" },
  { value: "mvp_no_users", label: "Ho un MVP, ma senza utenti" },
  { value: "mvp_with_users", label: "Ho un MVP usato da qualcuno" },
  { value: "paying_users", label: "Ho già utenti paganti" },
];

const CHANNEL_OPTIONS: RadioOption<TestChannel>[] = [
  { value: "linkedin", label: "LinkedIn (DM o post)" },
  { value: "email", label: "Email a freddo" },
  { value: "phone", label: "Telefono", hint: "Chiamate vocali dirette." },
  { value: "whatsapp", label: "WhatsApp", hint: "Messaggi 1-a-1 a contatti che hai già o puoi ottenere." },
  { value: "instagram_dm", label: "Instagram DM" },
  { value: "community", label: "Community online (Slack, Discord, Facebook)", hint: "Funziona solo se la community è davvero rilevante per il target." },
  { value: "cold_walk_in", label: "Di persona / a freddo offline" },
  { value: "other", label: "Altro" },
  { value: "unknown", label: "Non lo so ancora" },
];

const TOTAL_STEPS = 5;

function RequiredMark() {
  return <span className="text-primary ml-0.5">*</span>;
}

function TextareaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  required = false,
  optional = false,
  helper,
  longHint = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  optional?: boolean;
  helper?: string;
  longHint?: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium flex items-center gap-2">
        <span>
          {label}
          {required && <RequiredMark />}
        </span>
        {optional && (
          <span className="text-[10px] uppercase tracking-wider text-slate-500">opzionale</span>
        )}
      </label>
      {helper && <p className="text-xs text-slate-400">{helper}</p>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={2000}
        className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary/50"
      />
      {longHint && (
        <p className="text-[11px] text-slate-500">
          Prova a scrivere almeno 1–2 frasi concrete: la diagnosi migliora molto.
        </p>
      )}
    </div>
  );
}

function RadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | "";
  options: RadioOption<T>[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        {label}
        <RequiredMark />
      </label>
      <div className="grid gap-2">
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
                selected
                  ? "border-primary/60 bg-primary/10 text-white"
                  : "border-white/10 bg-white/5 text-slate-200 hover:border-white/25"
              }`}
            >
              <div className="font-medium">{o.label}</div>
              {o.hint && <div className="text-xs text-slate-400 mt-1">{o.hint}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepProgress({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i < step ? "bg-primary" : "bg-white/10"
          }`}
        />
      ))}
    </div>
  );
}

const LOADING_STEPS = [
  "Analizzo target e problema…",
  "Calcolo la diagnosi…",
  "Preparo il test 48h…",
];
const EXTRA_LOADING_STEP =
  "Ci siamo quasi… stiamo preparando il test più adatto alle tue risposte.";

function LoadingScreen() {
  const [idx, setIdx] = useState(0);
  const [showExtra, setShowExtra] = useState(false);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => Math.min(i + 1, LOADING_STEPS.length - 1));
    }, 4000);
    const extra = setTimeout(() => setShowExtra(true), 15000);
    return () => {
      clearInterval(t);
      clearTimeout(extra);
    };
  }, []);
  return (
    <FunnelLayout>
      <div className="max-w-xl mx-auto text-center space-y-8 py-16">
        <div className="mx-auto w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <div className="space-y-3">
          <h2 className="text-xl md:text-2xl font-semibold">
            Stiamo preparando il tuo Reality Check e il test da fare nelle prossime 48 ore.
          </h2>
          <ul className="space-y-1.5 text-sm">
            {LOADING_STEPS.map((s, i) => (
              <li
                key={s}
                className={
                  i < idx
                    ? "text-slate-500 line-through"
                    : i === idx
                    ? "text-white"
                    : "text-slate-500"
                }
              >
                {s}
              </li>
            ))}
            {showExtra && (
              <li className="text-secondary pt-2">{EXTRA_LOADING_STEP}</li>
            )}
          </ul>
          <p className="text-xs text-slate-500">Possono volerci fino a 30 secondi.</p>
        </div>
      </div>
    </FunnelLayout>
  );
}

export default function RealityCheck() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [inputs, setInputs] = useState<RealityCheckInputs>(EMPTY_INPUTS);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const draft = loadDraft();
    if (draft) {
      setStep(Math.min(Math.max(draft.step, 1), TOTAL_STEPS));
      setInputs({ ...EMPTY_INPUTS, ...draft.inputs });
      setEmail(draft.email ?? "");
      setConsent(!!draft.consent);
    }
  }, []);

  useEffect(() => {
    if (!hydrated.current) return;
    const d: FormDraft = { step, inputs, email, consent };
    saveDraft(d);
  }, [step, inputs, email, consent]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  function update<K extends keyof RealityCheckInputs>(key: K, value: RealityCheckInputs[K]) {
    setInputs((p) => ({ ...p, [key]: value }));
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const stepValid = useMemo(() => {
    switch (step) {
      case 1:
        return !!(inputs.idea?.trim() && inputs.target?.trim());
      case 2:
        return !!(
          inputs.problem?.trim() &&
          inputs.problem_frequency &&
          inputs.problem_urgency_cost &&
          inputs.current_behaviour_choice
        );
      case 3:
        return !!inputs.solution?.trim();
      case 4: {
        if (!inputs.validation_state || !inputs.test_channel) return false;
        if (inputs.test_channel === "other" && !inputs.test_channel_other?.trim()) return false;
        return true;
      }
      case 5:
        return emailValid;
      default:
        return false;
    }
  }, [step, inputs, emailValid]);


  async function handleSubmit() {
    if (!stepValid || loading) return;
    setError(null);
    setLoading(true);
    try {
      await submitRealityCheck({
        email: email.trim(),
        marketing_consent: consent,
        inputs,
      });
      navigate("/v2/reality-check/result");
    } catch (err: any) {
      setError(err?.message ?? "Si è verificato un errore. Riprova.");
      setLoading(false);
    }
  }

  if (loading) return <LoadingScreen />;

  return (
    <FunnelLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        <header className="space-y-3">
          <p className="text-xs uppercase tracking-widest text-secondary">Reality Check</p>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight">
            La tua idea regge un primo test reale?
          </h1>
          <p className="text-slate-300 text-sm">
            5 passaggi, ~5–7 minuti. Rispondi in modo concreto: la diagnosi vale quanto le risposte.
          </p>
          <StepProgress step={step} />
          <p className="text-xs text-slate-500">
            Alla fine riceverai:{" "}
            <span className="text-slate-400">verdetto · script di contatto · missione 48h</span>
          </p>
          <p className="text-xs text-slate-500">
            Step {step} di {TOTAL_STEPS}
          </p>
        </header>

        <GlassCard className="space-y-6">
          {step === 1 && (
            <>
              <TextareaField
                label="Descrivi la tua idea in una frase"
                value={inputs.idea}
                onChange={(v) => update("idea", v)}
                placeholder="Es. App che aiuta i piccoli ristoranti a gestire le prenotazioni via WhatsApp."
                required
                longHint
              />
              <TextareaField
                label="Chi è il tuo target specifico?"
                value={inputs.target}
                onChange={(v) => update("target", v)}
                placeholder="Es. Ristoratori indipendenti italiani con 1–2 locali, 30–55 anni."
                helper="Più sei specifico (segmento, dimensione, contesto), migliore sarà la diagnosi."
                required
              />
            </>
          )}

          {step === 2 && (
            <>
              <TextareaField
                label="Quale problema risolve davvero?"
                value={inputs.problem}
                onChange={(v) => update("problem", v)}
                placeholder="Es. Perdono prenotazioni perché rispondono in ritardo ai messaggi."
                required
                longHint
              />
              <RadioGroup
                label="Quanto spesso si presenta questo problema per il target?"
                value={inputs.problem_frequency}
                options={FREQUENCY_OPTIONS}
                onChange={(v) => update("problem_frequency", v)}
              />
              <RadioGroup
                label="Se questo problema sparisse domani, cosa cambierebbe per il target?"
                value={inputs.problem_urgency_cost}
                options={URGENCY_OPTIONS}
                onChange={(v) => update("problem_urgency_cost", v)}
              />
              <RadioGroup
                label="Come se la cava oggi il target con questo problema?"
                value={inputs.current_behaviour_choice}
                options={BEHAVIOUR_OPTIONS}
                onChange={(v) => update("current_behaviour_choice", v)}
              />
              <TextareaField
                label="Descrivi cosa fa o usa oggi (opzionale)"
                value={inputs.current_solution}
                onChange={(v) => update("current_solution", v)}
                placeholder="Es. Usano un foglio Excel e WhatsApp. Oppure: pagano una segreteria a 200€/mese."
                longHint
              />
            </>
          )}

          {step === 3 && (
            <>
              <TextareaField
                label="Qual è la tua soluzione proposta?"
                value={inputs.solution}
                onChange={(v) => update("solution", v)}
                placeholder="Es. Bot WhatsApp che conferma automaticamente le prenotazioni e avvisa il ristoratore."
                required
                rows={4}
                longHint
              />
              <TextareaField
                label="Cosa la rende diversa dalle alternative?"
                value={inputs.differentiation}
                onChange={(v) => update("differentiation", v)}
                placeholder="Es. Zero setup, funziona sul numero WhatsApp esistente del locale."
                optional
                rows={2}
              />
            </>
          )}

          {step === 4 && (
            <>
              <RadioGroup
                label="Hai già fatto qualcosa per testare l'idea?"
                value={inputs.validation_state}
                options={VALIDATION_OPTIONS}
                onChange={(v) => update("validation_state", v)}
              />
              <RadioGroup
                label="Dove trovi più facilmente il tuo target oggi?"
                value={inputs.test_channel}
                options={CHANNEL_OPTIONS}
                onChange={(v) => update("test_channel", v)}
              />
              {inputs.test_channel === "other" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Specifica il canale
                    <RequiredMark />
                  </label>
                  <input
                    type="text"
                    value={inputs.test_channel_other}
                    onChange={(e) => update("test_channel_other", e.target.value)}
                    placeholder="Es. Reddit r/restaurantowners, gruppo Telegram, evento di settore…"
                    maxLength={120}
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary/50"
                  />
                </div>
              )}
              <TextareaField
                label="Chi dovrebbe pagare e perché?"
                value={inputs.business_model}
                onChange={(v) => update("business_model", v)}
                placeholder="Es. Il ristoratore, abbonamento mensile a 29€ per locale."
                optional
                rows={2}
              />
            </>
          )}

          {step === 5 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  La tua email
                  <RequiredMark />
                </label>
                <p className="text-xs text-slate-400">
                  Ti inviamo qui il riepilogo della diagnosi.
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary/50"
                  required
                />
              </div>
              <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  Accetto di ricevere occasionalmente consigli pratici per founder via email (opzionale).
                </span>
              </label>
            </>
          )}

          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}
        </GlassCard>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← Indietro
          </button>

          {step < TOTAL_STEPS ? (
            <button
              type="button"
              disabled={!stepValid}
              onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
              className="inline-flex items-center justify-center gap-2 rounded-xl font-semibold px-6 py-3 text-sm text-primary-foreground bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.55)] hover:shadow-[0_18px_44px_-12px_hsl(var(--primary)/0.7)] hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Avanti
            </button>
          ) : (
            <button
              type="button"
              disabled={!stepValid}
              onClick={handleSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-xl font-semibold px-6 py-3 text-sm md:text-base text-primary-foreground bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.55)] hover:shadow-[0_18px_44px_-12px_hsl(var(--primary)/0.7)] hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Genera il mio Reality Check
            </button>
          )}
        </div>
      </div>
    </FunnelLayout>
  );
}
